/**
 * Renderer-adjacent biome + region config for the RegionMap / Route scenes.
 *
 * This is DATA, not rules: the deterministic encounter generation lives in
 * core/encounter (a pure function of `hash(worldSeed + inGameDate + nodeId)`),
 * and these definitions are simply the *input* that core consumes — weighted
 * encounter tables (which species, how rare, what level band) plus the soft
 * placeholder palette each biome paints with. They live in scenes/ because they
 * are presentation-facing and swappable; when the real ingested region data lands,
 * this module is the one place to replace.
 *
 * Decompression-first: every node is OPEN (no unlock gates / grind walls, per
 * CLAUDE.md), encounter rates are gentle, and level bands stay low for a relaxing
 * classic battle. Species ids use the dataset slugs; the encounter generator and
 * battle scene gracefully skip any id the (fixtures or generated) dataset lacks.
 *
 * SPECIES FOCUS (owner direction): wild encounters draw MOSTLY from Gen 1–2
 * (dex 1–251) for breadth, and from Gen 3 keep ONLY the FAMOUS species — the
 * pseudo-legendary lines (Bagon, Beldum) and the legendaries/mythicals (Regis,
 * the Eon duo, the weather trio, Jirachi, Deoxys) — as very-rare static spawns.
 * Generic Gen 3 species stay in the dataset/dex but are dropped from the wild
 * tables. The allow-list lives in `curatedSpecies.ts` and is unit-enforced.
 */
import type { BiomeData, BiomeKind, EncounterEntry, RegionNode } from '@/shared';

/** Presentation + data for one biome: the core encounter table plus the soft art. */
export interface BiomeDef extends BiomeData {
  /** One-line warm invitation shown on the map card. */
  readonly invitation: string;
  /** Soft ground fill (0xRRGGBB) for the placeholder overworld. */
  readonly ground: number;
  /** A complementary path/accent color. */
  readonly accent: number;
}

/** A region node with everything the map needs (open by default — no gates). */
export type RegionNodeDef = RegionNode;

/** Build a grass-slot encounter entry succinctly. */
function grass(speciesId: string, weight: number, min: number, max: number): EncounterEntry {
  return { speciesId, weight, levelBand: { min, max }, slot: 'grass' };
}

/** A surf/water-slot encounter entry. */
function surf(speciesId: string, weight: number, min: number, max: number): EncounterEntry {
  return { speciesId, weight, levelBand: { min, max }, slot: 'surf' };
}

/** A fishing-slot encounter entry (rod-cast water dwellers). */
function fishing(speciesId: string, weight: number, min: number, max: number): EncounterEntry {
  return { speciesId, weight, levelBand: { min, max }, slot: 'fishing' };
}

/** A cave-slot encounter entry. */
function cave(speciesId: string, weight: number, min: number, max: number): EncounterEntry {
  return { speciesId, weight, levelBand: { min, max }, slot: 'cave' };
}

/** A static-slot encounter entry — a single quiet, stationary presence (very rare). */
function staticEnc(speciesId: string, weight: number, min: number, max: number): EncounterEntry {
  return { speciesId, weight, levelBand: { min, max }, slot: 'static' };
}

/**
 * The biome catalogue. Species ids are real dataset slugs; the encounter generator
 * picks among whatever the live dataset actually has (the fixtures cover the
 * starters + pikachu/haunter, the generated set covers Gen 1–3), and the Route /
 * Battle scenes skip any species the dataset is missing — so this never crashes a
 * thin fixtures build.
 */
const BIOMES: readonly BiomeDef[] = [
  // ── 1. Sunny Meadow — the gentle starter biome (common starters + a wink of rare). ──
  {
    id: 'meadow',
    kind: 'meadow' as BiomeKind,
    name: 'Sunny Meadow',
    invitation: 'Tall grass, soft breeze, friendly faces.',
    encounterRate: 0.28,
    tilesetKey: 'placeholder-meadow',
    ground: 0x4a6b4a,
    accent: 0x9ad17f,
    encounters: {
      entries: [
        // common
        grass('pikachu', 24, 3, 6),
        grass('pidgey', 22, 3, 6),
        // uncommon — meadow-exclusive Hoppip family hangs in the open grass
        grass('bulbasaur', 16, 3, 6),
        grass('hoppip', 14, 3, 6),
        // rare
        grass('squirtle', 8, 3, 6),
        grass('charmander', 8, 3, 6),
        // very-rare — a passing wonder
        grass('haunter', 4, 5, 7),
        staticEnc('chansey', 2, 6, 8),
        // Legendaries no longer sit in the tables — they surface ONLY via the rare,
        // high-level ROAMING overlay (core/legendary). Latias drifts toward the meadow.
      ],
    },
  },
  // ── 2. Whispering Forest — cool bug/grass shade (Caterpie/Weedle line exclusive). ──
  {
    id: 'whispering-forest',
    kind: 'forest' as BiomeKind,
    name: 'Whispering Forest',
    invitation: 'Cool shade and the rustle of bug-types.',
    encounterRate: 0.3,
    tilesetKey: 'placeholder-forest',
    ground: 0x355238,
    accent: 0x7fae8a,
    encounters: {
      entries: [
        // common — forest is THE place for the early bug lines
        grass('caterpie', 24, 4, 7),
        grass('weedle', 22, 4, 7),
        // uncommon
        grass('bulbasaur', 18, 4, 7),
        grass('oddish', 14, 4, 7),
        // rare
        grass('pikachu', 9, 4, 7),
        // very-rare — the forest guardian, forest-exclusive
        grass('scyther', 4, 8, 11),
        // Legendaries (Celebi, Latios, Mew) surface only via the roaming overlay —
        // the forest is where they drift toward (see core/legendary HOME_KIND).
      ],
    },
  },
  // ── 3. Still Lake — open water; surf + fishing draws. ──
  {
    id: 'still-lake',
    kind: 'lake' as BiomeKind,
    name: 'Still Lake',
    invitation: 'Glassy water; something splashes now and then.',
    encounterRate: 0.26,
    tilesetKey: 'placeholder-lake',
    ground: 0x35506b,
    accent: 0x86b8d6,
    encounters: {
      entries: [
        // surf — the broad water draws
        surf('squirtle', 24, 4, 7),
        surf('psyduck', 20, 4, 7),
        // fishing — a rod by the reeds; Magikarp is everywhere but a patient cast finds Goldeen
        fishing('magikarp', 22, 4, 6),
        fishing('goldeen', 14, 5, 8),
        // a little grass at the shoreline
        grass('bulbasaur', 10, 4, 7),
        // very-rare — the lake spirit, lake-exclusive
        surf('dratini', 5, 7, 10),
        staticEnc('lapras', 2, 9, 12),
        // Legendaries (Suicune, Regice) drift toward the lake — they surface only via
        // the rare, high-level roaming overlay, never as a low-level table entry.
      ],
    },
  },
  // ── 4. Quiet Cavern — hushed echoes; cave-slot rock/ghost dwellers. ──
  {
    id: 'quiet-cavern',
    kind: 'cave' as BiomeKind,
    name: 'Quiet Cavern',
    invitation: 'Echoes and a hush; rarer friends rest here.',
    encounterRate: 0.24,
    tilesetKey: 'placeholder-cave',
    ground: 0x3a3550,
    accent: 0xb6a6e9,
    encounters: {
      entries: [
        // common — cave staples (Zubat/Geodude exclusive to rocky dark)
        cave('zubat', 24, 5, 8),
        cave('geodude', 22, 5, 8),
        // uncommon
        cave('haunter', 14, 6, 9),
        // rare
        cave('onix', 10, 6, 9),
        cave('pikachu', 8, 5, 8),
        // very-rare — a deep-dweller, cavern-exclusive (Cubone keens in the dark)
        cave('cubone', 4, 8, 11),
        staticEnc('larvitar', 2, 8, 11),
        // famous Gen 3 — Beldum, the iron mineral, sleeps in the deep stone (a pseudo-
        // legendary, NOT flagged legendary, so it stays a real catchable table entry).
        staticEnc('beldum', 1, 14, 18),
        // Legendaries (Articuno, Regirock) drift toward the cave — roaming overlay only.
      ],
    },
  },
  // ── 5. Pinewood Deep — old-growth deep forest; quieter, higher level band. ──
  {
    id: 'deep-forest',
    kind: 'forest' as BiomeKind,
    name: 'Pinewood Deep',
    invitation: 'Old-growth pines and a green, watchful quiet.',
    encounterRate: 0.27,
    tilesetKey: 'placeholder-deep-forest',
    ground: 0x223a2a,
    accent: 0x5e9c6a,
    encounters: {
      entries: [
        // common — the deep wood favors heavier grass dwellers
        grass('oddish', 24, 8, 12),
        grass('bellsprout', 20, 8, 12),
        // uncommon
        grass('exeggcute', 16, 9, 13),
        grass('pinsir', 12, 9, 13),
        // rare
        grass('tangela', 8, 10, 14),
        // very-rare — deep-forest-exclusive ancients
        grass('heracross', 4, 11, 15),
        staticEnc('sudowoodo', 2, 12, 15),
        // (The Johto beasts now roam via the legendary overlay, not the table.)
      ],
    },
  },
  // ── 6. Windrest Ridge — mountain slope; thin air, fighting/flying/rock. ──
  {
    id: 'mountain-slope',
    kind: 'mountain' as BiomeKind,
    name: 'Windrest Ridge',
    invitation: 'A bracing climb; wings circle the high crags.',
    encounterRate: 0.25,
    tilesetKey: 'placeholder-mountain',
    ground: 0x5b4f43,
    accent: 0xd6b48a,
    encounters: {
      entries: [
        // common — the slopes are loud with Spearow and Machop
        grass('spearow', 24, 10, 14),
        grass('machop', 20, 10, 14),
        // uncommon
        grass('geodude', 16, 10, 14),
        grass('rhyhorn', 12, 11, 15),
        // rare
        grass('larvitar', 8, 12, 16),
        // very-rare — mountain-exclusive sky kings
        grass('aerodactyl', 4, 13, 17),
        staticEnc('skarmory', 2, 14, 17),
        // famous Gen 3 — Bagon hurls itself off the crags dreaming of flight (a pseudo-
        // legendary, NOT flagged legendary, so it stays a real catchable table entry).
        staticEnc('bagon', 1, 14, 18),
        // Legendaries (Zapdos, Moltres, Entei, Rayquaza, Groudon, Ho-Oh) drift toward
        // the high crags — they surface only via the rare, high-level roaming overlay.
      ],
    },
  },
  // ── 7. Sunken Ruins — old stone halls; psychic/ghost, eerie static dwellers. ──
  {
    id: 'old-ruins',
    kind: 'ruins' as BiomeKind,
    name: 'Sunken Ruins',
    invitation: 'Mossy stone and old murals; the air feels watched.',
    encounterRate: 0.22,
    tilesetKey: 'placeholder-ruins',
    ground: 0x4a4660,
    accent: 0xc9a8e6,
    encounters: {
      entries: [
        // common — ruins crawl with gastly and the relic-bound Unown
        grass('gastly', 24, 12, 16),
        grass('unown', 18, 12, 16),
        // uncommon
        grass('abra', 16, 12, 16),
        grass('natu', 14, 12, 16),
        // rare
        grass('misdreavus', 8, 13, 17),
        // very-rare — ruins-exclusive relics (the fossil-bound Kabuto stirs in the stone)
        grass('kabuto', 5, 13, 17),
        // a hush of something rarer prowling the dark halls — the ruins' scarce signature
        staticEnc('sneasel', 2, 14, 18),
        // Legendaries (Mewtwo, Registeel, Jirachi, Deoxys) drift toward the old ruins —
        // they surface only via the rare, high-level roaming overlay (core/legendary).
      ],
    },
  },
  // ── 8. Emberfall Ashland — volcanic; fire/ground, the highest base band. ──
  {
    id: 'volcanic-ashland',
    kind: 'mountain' as BiomeKind,
    name: 'Emberfall Ashland',
    invitation: 'Warm ash drifts; embers wander the black sand.',
    encounterRate: 0.24,
    tilesetKey: 'placeholder-volcanic',
    ground: 0x5a322a,
    accent: 0xef8a5a,
    encounters: {
      entries: [
        // common — the ashland is alive with fire-types
        grass('vulpix', 24, 14, 18),
        grass('growlithe', 20, 14, 18),
        // uncommon
        grass('ponyta', 16, 14, 18),
        grass('slugma', 12, 15, 19),
        // rare
        grass('magby', 8, 15, 19),
        // very-rare — ashland-exclusive ember spirits
        grass('houndour', 5, 16, 20),
        staticEnc('magmar', 2, 18, 21),
        // (Fire legendaries — Moltres, Entei, Groudon — roam the mountain/ashland kind
        //  via the legendary overlay, never as a low-level table entry.)
      ],
    },
  },
];

/** O(1) lookup of a biome by id. */
const BIOME_BY_ID = new Map(BIOMES.map((b) => [b.id, b]));

export function biomeById(id: string): BiomeDef | undefined {
  return BIOME_BY_ID.get(id);
}

export function allBiomes(): readonly BiomeDef[] {
  return BIOMES;
}

/**
 * The region graph: one open node per biome (all reachable, no unlock gates). Node
 * ids double as the per-node encounter seed key (core/encounter hashes nodeId), so
 * each destination rolls its own daily route.
 */
const NODES: readonly RegionNodeDef[] = [
  {
    id: 'node-meadow',
    name: 'Sunny Meadow',
    biomeId: 'meadow',
    x: 0,
    y: 0,
    connections: ['node-forest', 'node-lake'],
    unlock: null,
  },
  {
    id: 'node-forest',
    name: 'Whispering Forest',
    biomeId: 'whispering-forest',
    x: 1,
    y: 0,
    connections: ['node-meadow', 'node-cavern', 'node-deep-forest'],
    unlock: null,
  },
  {
    id: 'node-lake',
    name: 'Still Lake',
    biomeId: 'still-lake',
    x: 0,
    y: 1,
    connections: ['node-meadow', 'node-cavern'],
    unlock: null,
  },
  {
    id: 'node-cavern',
    name: 'Quiet Cavern',
    biomeId: 'quiet-cavern',
    x: 1,
    y: 1,
    connections: ['node-forest', 'node-lake', 'node-mountain'],
    unlock: null,
  },
  {
    id: 'node-deep-forest',
    name: 'Pinewood Deep',
    biomeId: 'deep-forest',
    x: 2,
    y: 0,
    connections: ['node-forest', 'node-ruins'],
    unlock: null,
  },
  {
    id: 'node-mountain',
    name: 'Windrest Ridge',
    biomeId: 'mountain-slope',
    x: 2,
    y: 1,
    connections: ['node-cavern', 'node-volcano'],
    unlock: null,
  },
  {
    id: 'node-ruins',
    name: 'Sunken Ruins',
    biomeId: 'old-ruins',
    x: 3,
    y: 0,
    connections: ['node-deep-forest', 'node-volcano'],
    unlock: null,
  },
  {
    id: 'node-volcano',
    name: 'Emberfall Ashland',
    biomeId: 'volcanic-ashland',
    x: 3,
    y: 1,
    connections: ['node-mountain', 'node-ruins'],
    unlock: null,
  },
];

const NODE_BY_ID = new Map(NODES.map((n) => [n.id, n]));

export function regionNodeById(id: string): RegionNodeDef | undefined {
  return NODE_BY_ID.get(id);
}

/** The whole region the daily expedition picks from. */
export const REGION = {
  id: 'parallel-routes',
  name: 'The Region',
  nodes: NODES,
  startNodeId: 'node-meadow',
} as const;
